import { useCallback, useEffect, useState } from "react";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import baseUrl from "../../../api/baseUrl";
import MessageLengthHint from "./MessageLengthHint";
import { PanelCard, SectionHeader } from "../components/accountUi";

type EventTemplate = {
  type: string;
  label: string;
  description: string;
  placeholders: string[];
  custom: boolean;
  subject: string;
  body: string;
  enabled: boolean;
};

function errorMessageFrom(error: any, fallback: string) {
  return error?.response?.data?.message || fallback;
}

function EventTemplateCard({ template, onChanged }: { template: EventTemplate; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setSubject(template.subject);
    setBody(template.body);
    setError(null);
    setEditing(true);
  };

  const save = (payload: Record<string, unknown>, after?: () => void) => {
    setSaving(true);
    setError(null);
    baseUrl
      .put(`/client/account/email-event-templates/${template.type}`, payload)
      .then(() => {
        after?.();
        onChanged();
      })
      .catch((err) => setError(errorMessageFrom(err, "Could not save the template.")))
      .finally(() => setSaving(false));
  };

  const reset = () => {
    if (!window.confirm("Go back to the default wording for this email?")) return;
    setSaving(true);
    setError(null);
    baseUrl
      .delete(`/client/account/email-event-templates/${template.type}`)
      .then(() => {
        setEditing(false);
        onChanged();
      })
      .catch((err) => setError(errorMessageFrom(err, "Could not reset the template.")))
      .finally(() => setSaving(false));
  };

  return (
    <div
      className={`rounded-2xl border p-4 ${
        template.enabled
          ? "border-emerald-200/80 bg-emerald-50/30 dark:border-emerald-800/50 dark:bg-emerald-950/10"
          : "border-slate-200/80 dark:border-slate-700/80"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-slate-800 dark:text-white">{template.label}</p>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            template.custom
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300"
          }`}
        >
          {template.custom ? "Your wording" : "Default wording"}
        </span>
        <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={template.enabled}
            disabled={saving}
            onChange={(e) => save({ enabled: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
          />
          Send
        </label>
      </div>
      <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{template.description}</p>

      {editing ? (
        <div className="mt-3 space-y-2">
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={7}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          />
          <MessageLengthHint text={body} limit={160} unit="email" />
          <div className="flex flex-wrap gap-1">
            {template.placeholders.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setBody((prev) => `${prev}{{${key}}}`)}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
              >
                {`{{${key}}}`}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => save({ subject, body }, () => setEditing(false))}
              disabled={saving || !subject.trim() || !body.trim()}
              className="rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:border-slate-600 dark:text-slate-300"
            >
              Cancel
            </button>
            {template.custom ? (
              <button
                type="button"
                onClick={reset}
                disabled={saving}
                className="rounded-xl px-3 py-1.5 text-xs font-semibold text-rose-600 hover:underline"
              >
                Use default wording
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <p className="mt-2 text-xs font-semibold text-slate-700 dark:text-slate-200">{template.subject}</p>
          <p className="mt-1 whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">
            {template.body}
            <MessageLengthHint text={template.body} limit={160} unit="email" />
          </p>
          <button
            type="button"
            onClick={startEdit}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
            Edit
          </button>
        </>
      )}

      {error ? <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">{error}</p> : null}
    </div>
  );
}

/**
 * Automatic emails, like the regular SMS templates: sent on registration,
 * booking, reschedule, reminders and invoices to patients with an email.
 */
export default function EmailEventTemplatesPanel() {
  const [templates, setTemplates] = useState<EventTemplate[]>([]);

  const load = useCallback(() => {
    baseUrl
      .get("/client/account/email-event-templates")
      .then((res) => setTemplates(res?.data?.success ? res.data.data ?? [] : []))
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PanelCard className="overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
        <SectionHeader
          icon={MarkEmailReadOutlinedIcon}
          title="Automatic Emails"
          subtitle="Sent with the regular SMS to patients who have an email address."
        />
      </div>
      <div className="grid gap-3 p-5 lg:grid-cols-2">
        {templates.map((template) => (
          <EventTemplateCard key={template.type} template={template} onChanged={load} />
        ))}
      </div>
    </PanelCard>
  );
}
